import React, { useEffect } from 'react';

import { CTA, Spinner, TextCustom, TextWeight, ThemeProvider } from '@vw-marketing/us-components';

import { applyTextTransformations } from '../../../../shared/utils/applyTextTransformations';
import { CompleteVehicleCard } from '../../../../shared/components/vehicle-card/complete-vehicle-card/CompleteVehicleCard';
import { getUniqueExternalSubscriptions } from '../../../../shared/utils/general';
import { InformationBox } from '../../../../shared/components/information-box/InformationBox';
import { PartialVehicleCard } from '../../../../shared/components/vehicle-card/partial-vehicle-card/PartialVehicleCard';
import { Plan, SiriusXMSubscription, Vehicles } from '../../../../shared/typings/main';
import { SubscriptionCard } from '../../../../shared/components/subscription-card/SubscriptionCard';
import {
  StyledDeactiveAccountNoVehicleUserContainer,
  StyledDeactiveAccountNoVehicleUserCTAContainer,
  StyledUserVehiclesAndServicesContainer,
  StyledUserSubscriptionsContainer,
  StyledUserVehiclesContainer,
  StyledInformationContainer,
  StyledCardsContainer,
  StyledButtonsContainer,
} from './styles';
import ContentList from '../../../../shared/components/content-list';
import ContentListItem from '../../../../shared/components/content-list-item';
import {
  onVWBasicStepLoadEvent,
  onVWBasicButtonClickWithProcessEvent,
  onVWBasicLayerLoadEvent,
} from '../../../../shared/tagging/tagging-helpers';
import { UserData } from '../../../../shared/typings/main';

interface DeactivateAccountViewProps {
  translationHandler: (key: string) => string;
  trackingManager: any;
  isDesktop: boolean;
  isCanada: boolean;
  isFrench: boolean;
  user: UserData | null;
  logout: () => void;
  userRole: 'primary' | 'additional' | 'mixed' | 'no-vehicle' | undefined;
  handleDeactivateConfirmation: () => void;
  onGoBack: () => void;
}

export const DeactivateAccountView: React.FC<DeactivateAccountViewProps> = ({
  translationHandler,
  trackingManager,
  isDesktop,
  isCanada,
  isFrench,
  user,
  logout,
  userRole,
  handleDeactivateConfirmation,
  onGoBack,
}) => {

  // Track deactivate account layer load when component mounts
  useEffect(() => {
    try {
      onVWBasicStepLoadEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: 'Personal Information and Security - Deactivate Account',
        StepNumber: '4.4',
      });

      if (isCanada) {
        onVWBasicLayerLoadEvent(trackingManager, 'Security - Deactivate myVW Account');
      }
    } catch (error) {
      console.warn('Error tracking deactivate account step load:', error);
    }
  }, [trackingManager]);

  const privacyPolicyUrl = isCanada
    ? (isFrench ? 'https://www.vw.ca/fr/politique-de-confidentialite.html' : 'https://www.vw.ca/en/privacy-policy.html')
    : 'https://privacy.vwgoa.com/';

  const handleGoBackAction = () => {
    try {
      // Track Go Back button click
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: 'Personal Information and Security - Deactivate',
        StepNumber: '4.4',
      }, { name: 'Go Back' });
    } catch (error) {
      console.warn('Error tracking Go Back button click:', error);
    }
    onGoBack();
  };

  const handleLogoutAction = () => {
    try {
      // Track Logout button click
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: 'Personal Information and Security - Deactivate',
        StepNumber: '4.4',
      }, { name: 'Logout' });
    } catch (error) {
      console.warn('Error tracking Logout button click:', error);
    }
    logout();
  };

  const handleContinueAction = () => {
    try {
      // Track Continue button click
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: 'Personal Information and Security - Deactivate',
        StepNumber: '4.4',
      }, { name: 'Continue' });
    } catch (error) {
      console.warn('Error tracking Continue button click:', error);
    }
    handleDeactivateConfirmation();
  };

  const userVehicles: Vehicles[] = user?.vehicles || [];

  // Get unique external subscriptions (T-Mobile, Verizon)
  const allExternalSubscriptions = getUniqueExternalSubscriptions(userVehicles);
  const externalSubscriptions = allExternalSubscriptions.filter((_sub: Plan) => {
    if (isCanada) return false;
    return true;
  });
  const hasSiriusXMSubscription = isCanada
    ? undefined
    : userVehicles.find((vehicle: Vehicles) => vehicle.siriusXMSubscription?.audioSubscription !== null) as SiriusXMSubscription | undefined;

  if (!userRole) return null;

  const spinnerContainer = () => (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        width: '100%'
      }}
    >
      <Spinner />
    </div>
  );

  const deactivateButtons = () => (
    <StyledButtonsContainer>
      <CTA tag='button' stretchContent={!isDesktop} emphasis='secondary' onClick={handleGoBackAction}>
        {translationHandler('accountSettings.deactivate.myvwaccount.goBack.label')}
      </CTA>
      <CTA tag='button' stretchContent={!isDesktop} onClick={handleContinueAction}>
        {translationHandler('accountSettings.deactivate.myvwaccount.continue.label')}
      </CTA>
    </StyledButtonsContainer>
  );

  const subscriptionsSection = externalSubscriptions.length > 0 && (
    <StyledUserSubscriptionsContainer>
      <InformationBox
        title={translationHandler('accountSettings.deactivate.myvwaccount.services.title')}
        description={translationHandler('accountSettings.deactivate.myvwaccount.subscriptions.description')}
      />
      <StyledCardsContainer hasDescription={true}>
        {externalSubscriptions.map((subscription: Plan, index: number) => (
          <SubscriptionCard
            id={`${subscription.package.sku || index + 1}`}
            planName={subscription.package.name || ''}
            providerName={subscription.provider.name || ''}
            isCanada={isCanada}
            translationHandler={translationHandler}
          />
        ))}
        {hasSiriusXMSubscription && <SubscriptionCard id='SiriusXM-Subscription-Card' planName='SiriusXM' providerName='SiriusXM' translationHandler={translationHandler} />}
      </StyledCardsContainer>
    </StyledUserSubscriptionsContainer>
  );

  switch (userRole) {
    case 'mixed':
      return (
        <ThemeProvider theme="main">
          <StyledUserVehiclesAndServicesContainer>
            <ContentList title={translationHandler('accountSettings.deactivate.myvwaccount.primary.user.title')}>
              <ContentListItem>
                {applyTextTransformations(translationHandler('accountSettings.deactivate.myvwaccount.mixed.user.first.content'), ['myVW'])}
              </ContentListItem>
              <ContentListItem>
                {translationHandler('accountSettings.deactivate.myvwaccount.mixed.user.second.content.part.one')}
                <TextCustom style={{ fontWeight: TextWeight.bold }}>
                  {translationHandler('accountSettings.deactivate.myvwaccount.mixed.user.second.content.bold')}
                </TextCustom>
                {applyTextTransformations(translationHandler('accountSettings.deactivate.myvwaccount.mixed.user.second.content.part.two'), ['myVW'])}
              </ContentListItem>
              <ContentListItem>
                {translationHandler('accountSettings.deactivate.myvwaccount.mixed.user.third.content.part.one')}
                <TextCustom style={{ fontWeight: TextWeight.bold }}>
                  {translationHandler('accountSettings.deactivate.myvwaccount.mixed.user.third.content.bold')}
                </TextCustom>
                {translationHandler('accountSettings.deactivate.myvwaccount.mixed.user.third.content.part.two')}
              </ContentListItem>
              <ContentListItem>
                {translationHandler('accountSettings.deactivate.myvwaccount.personal.data.part.one')}
                <CTA tag="a" emphasis="tertiary" href={privacyPolicyUrl}>{translationHandler('accountSettings.deactivate.myvwaccount.personal.data.link')}</CTA>
                {translationHandler('accountSettings.deactivate.myvwaccount.personal.data.part.two')}
              </ContentListItem>
            </ContentList>
            <StyledUserVehiclesContainer>
              <InformationBox title={translationHandler('accountSettings.deactivate.myvwaccount.lose.title')} />
              <StyledCardsContainer hasDescription={false}>
                {userVehicles.length === 0 ?
                  spinnerContainer()
                  : userVehicles.map((vehicle: Vehicles) => {

                    if (vehicle.userRole === 'Additional User')
                      return <PartialVehicleCard id={vehicle.vehicleId} vehicle={vehicle} userRole={vehicle.userRole} />;

                    // Show PartialVehicleCard if there are no additional users (only primary)
                    // AND there are no internal subscriptions
                    if (vehicle.associatedUsers.length <= 1 && vehicle.internalSubscriptions.length === 0)
                      return <PartialVehicleCard id={vehicle.vehicleId} vehicle={vehicle} userRole={vehicle.userRole} />;

                    return <CompleteVehicleCard id={vehicle.vehicleId} vehicle={vehicle} translationHandler={translationHandler} />;
                  })}
              </StyledCardsContainer>
            </StyledUserVehiclesContainer>
            {subscriptionsSection}
            {deactivateButtons()}
          </StyledUserVehiclesAndServicesContainer>
        </ThemeProvider>
      );
    case 'primary':
      return (
        <ThemeProvider theme="main">
          <StyledUserVehiclesAndServicesContainer>
            <ContentList title={translationHandler('accountSettings.deactivate.myvwaccount.primary.user.title')}>
              <ContentListItem>{applyTextTransformations(translationHandler('accountSettings.deactivate.myvwaccount.primary.user.first.content'), ['myVW'])}</ContentListItem>
              <ContentListItem>{applyTextTransformations(translationHandler('accountSettings.deactivate.myvwaccount.primary.user.second.content'), ['myVW'])}</ContentListItem>
              <ContentListItem>
                {translationHandler('accountSettings.deactivate.myvwaccount.personal.data.part.one')}
                <CTA tag="a" emphasis="tertiary" href={privacyPolicyUrl}>{translationHandler('accountSettings.deactivate.myvwaccount.personal.data.link')}</CTA>
                {translationHandler('accountSettings.deactivate.myvwaccount.personal.data.part.two')}
              </ContentListItem>
            </ContentList>
            <StyledUserVehiclesContainer>
              <StyledInformationContainer>
                <InformationBox title={translationHandler('accountSettings.deactivate.myvwaccount.lose.title')} />
              </StyledInformationContainer>
              <StyledCardsContainer hasDescription={false}>
                {userVehicles.length === 0 ? spinnerContainer() : userVehicles.map((vehicle: Vehicles) => {
                  if (vehicle.associatedUsers.length > 0 || vehicle.internalSubscriptions.length > 0) return <CompleteVehicleCard id={vehicle.vehicleId} vehicle={vehicle} translationHandler={translationHandler} />;
                  return <PartialVehicleCard id={vehicle.vehicleId} vehicle={vehicle} userRole={vehicle.userRole} />;
                })}
              </StyledCardsContainer>
            </StyledUserVehiclesContainer>
            {subscriptionsSection}
            {deactivateButtons()}
          </StyledUserVehiclesAndServicesContainer>
        </ThemeProvider>
      );
    case 'additional':
      return (
        <ThemeProvider theme="main">
          <StyledUserVehiclesAndServicesContainer>
            <ContentList title={translationHandler('accountSettings.deactivate.myvwaccount.primary.user.title')}>
              <ContentListItem>{applyTextTransformations(translationHandler('accountSettings.deactivate.myvwaccount.additional.user.first.content'), ['myVW'])}</ContentListItem>
              <ContentListItem>{translationHandler('accountSettings.deactivate.myvwaccount.additional.user.second.content')}</ContentListItem>
              <ContentListItem>
                {translationHandler('accountSettings.deactivate.myvwaccount.personal.data.part.one')}
                <CTA tag="a" emphasis="tertiary" href={privacyPolicyUrl}>{translationHandler('accountSettings.deactivate.myvwaccount.personal.data.link')}</CTA>
                {translationHandler('accountSettings.deactivate.myvwaccount.personal.data.part.two')}
              </ContentListItem>
            </ContentList>
            <StyledUserVehiclesContainer>
              <InformationBox title={translationHandler('accountSettings.deactivate.myvwaccount.lose.title')} description={translationHandler('accountSettings.deactivate.myvwaccount.lose.description')} />
              <StyledCardsContainer hasDescription={true}>
                {userVehicles.length === 0 ? spinnerContainer() : userVehicles.map((vehicle: Vehicles) => (
                  <PartialVehicleCard id={vehicle.vehicleId} vehicle={vehicle} userRole={vehicle.userRole} />
                ))}
              </StyledCardsContainer>
            </StyledUserVehiclesContainer>
            {subscriptionsSection}
            {deactivateButtons()}
          </StyledUserVehiclesAndServicesContainer>
        </ThemeProvider>
      );
    case 'no-vehicle':
      return (
        <ThemeProvider theme="main">
          <StyledDeactiveAccountNoVehicleUserContainer>
            <ContentList>
              <ContentListItem hyphen={false}>
                {applyTextTransformations(translationHandler('accountSettings.deactivate.myvwaccount.no.vehicle.user.first.content.part.one'), ['myVW'])}
                <TextCustom style={{ fontWeight: TextWeight.bold }}>
                  {translationHandler('accountSettings.deactivate.myvwaccount.no.vehicle.user.first.content.bold')}
                </TextCustom>
                {applyTextTransformations(translationHandler('accountSettings.deactivate.myvwaccount.no.vehicle.user.first.content.part.two'), ['myVW'])}
              </ContentListItem>
              <ContentListItem hyphen={false}>
                {translationHandler('accountSettings.deactivate.myvwaccount.personal.data.part.one.alternative')}
                <CTA tag="a" emphasis="tertiary" href={privacyPolicyUrl}>{translationHandler('accountSettings.deactivate.myvwaccount.personal.data.link')}</CTA>
                {translationHandler('accountSettings.deactivate.myvwaccount.personal.data.part.two')}
              </ContentListItem>
            </ContentList>
            <StyledDeactiveAccountNoVehicleUserCTAContainer>
              <CTA tag="button" emphasis="tertiary" onClick={handleGoBackAction} stretchContent={false}>
                {translationHandler('accountSettings.deactivate.myvwaccount.no.vehicle.user.go.back')}
              </CTA>
              <CTA tag="button" emphasis="secondary" onClick={handleLogoutAction} stretchContent={false}>
                {translationHandler('accountSettings.deactivate.myvwaccount.no.vehicle.user.logout')}
              </CTA>
            </StyledDeactiveAccountNoVehicleUserCTAContainer>
          </StyledDeactiveAccountNoVehicleUserContainer>
        </ThemeProvider>
      );
    default:
      return (
        <ThemeProvider theme="main">
          <></>
        </ThemeProvider>
      );
  }
};